import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { Package, Search, Loader2 } from 'lucide-react';

const SB = { 'Picked Up': 'bg-blue-500/10 text-blue-400', 'In Storage': 'bg-emerald-500/10 text-emerald-400', Delivered: 'bg-purple-500/10 text-purple-400', Missing: 'bg-red-500/10 text-red-400', Damaged: 'bg-amber-500/10 text-amber-400' };
const CATS = ['All', 'Boxes', 'Furniture', 'Electronics', 'Appliances', 'Bags', 'Miscellaneous'];
const STATS = ['All', 'Picked Up', 'In Storage', 'Delivered', 'Missing', 'Damaged'];

export default function Inventory() {
  const ctx = useOutletContext();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => { adminApi.getInventory().then(d => { setData(d); setLoading(false); }); }, []);

  const filtered = data.filter(item => {
    if (catFilter !== 'All' && item.item_category !== catFilter) return false;
    if (statusFilter !== 'All' && item.status !== statusFilter) return false;
    if (search && !item.student_name.toLowerCase().includes(search.toLowerCase()) && !item.item_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  return (
    <div data-testid="admin-inventory">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Inventory Management</h1>
      <p className={`text-sm ${ctx.textSecondary} mb-6`}>{data.length} items tracked</p>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(SB).map(([status, cls]) => {
          const count = data.filter(i => i.status === status).length;
          return (
            <div key={status} className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
              <p className={`text-xs ${cls} font-medium`}>{status}</p>
              <p className="text-xl font-bold mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4 mb-4`}>
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${ctx.textSecondary}`} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items or students..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className={`px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent`}>
            {CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent`}>
            {STATS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className={`border-b ${ctx.borderColor}`}>
            {['Reservation', 'Student', 'Item', 'Category', 'Qty', 'Storage Unit', 'Status'].map(h => (
              <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{filtered.map(item => (
            <tr key={item._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
              <td className="px-4 py-3 font-mono text-xs">{item.reservation_number}</td>
              <td className="px-4 py-3 text-sm">{item.student_name}</td>
              <td className="px-4 py-3 text-sm font-medium">{item.item_name}</td>
              <td className="px-4 py-3 text-xs">{item.item_category}</td>
              <td className="px-4 py-3 text-center">{item.item_count}</td>
              <td className="px-4 py-3 text-xs font-mono">{item.storage_unit}</td>
              <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${SB[item.status] || ''}`}>{item.status}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
